// QBR template — a generic Quarterly Business Review deck as a typed SlideDeck.
// Twelve slides covering the canonical QBR arc: cover → agenda → headline →
// scorecard → trend → wins → deep-dive → commercial → risks → asks → next
// quarter → close. Content is realistic illustrative sample data so the preview
// reads as a real review; swap in live analytics before any customer use.
import type { SlideDeck } from '../slide-model';

export const qbrDeck: SlideDeck = {
  schemaVersion: 2,
  meta: {
    title: 'Quarterly Business Review',
    subtitle: 'Q3 FY26',
    author: 'RDx · Customer Success',
    date: 'Q3 FY26',
    theme: 'workplace-slate',
    fontset: 'inter',
    brand: '#1E2A3A',
  },
  slides: [
    {
      id: 'qbr-01', kind: 'cover', label: '01 · Cover', coverStyle: 'classic',
      eyebrow: 'Quarterly Business Review',
      title: 'Q3 FY26 in review',
      sub: 'Workplace by Ricoh · EMEA · Reviewed with Maya Lindqvist, Customer Success',
      byline: 'Prepared by Tom Shaw, Commercial & Operations',
      foot: 'Q3 FY26',
    },
    {
      id: 'qbr-02', kind: 'agenda', label: '02 · Agenda', layout: 'two-col',
      eyebrow: 'Agenda', title: 'What we will cover',
      items: [
        { t: 'The quarter at a glance', n: 'Headline result and how we tracked against plan.' },
        { t: 'Performance vs targets', n: 'Core KPIs and the trend line.', active: true },
        { t: 'Wins and adoption', n: 'What shipped and what landed.' },
        { t: 'Pipeline and renewals', n: 'Commercial health and what is in flight.' },
        { t: 'Risks and asks', n: 'What needs a decision from this room.' },
        { t: 'Next quarter', n: 'The plan and the success measures.' },
      ],
    },
    {
      id: 'qbr-03', kind: 'bignum', label: '03 · Headline', layout: 'centred',
      title: 'The quarter in one number',
      lede: 'A strong quarter, driven by adoption gains and two strategic expansions.',
      value: '112%', vlabel: 'of quarterly target delivered', delta: '+14 pts vs Q2',
      down: false,
    },
    {
      id: 'qbr-04', kind: 'kpi', label: '04 · Scorecard', layout: 'grid-2x2',
      eyebrow: 'Performance', title: 'Against our targets',
      lede: 'The four measures we committed to at the start of the quarter.',
      kpis: [
        { label: 'Active users', value: '8,420', delta: '+11%', up: true, spark: [7100, 7350, 7600, 7900, 8150, 8420] },
        { label: 'Net revenue retention', value: '108%', delta: '+3 pts', up: true, spark: [102, 103, 104, 105, 106, 108] },
        { label: 'Adoption (DAU/MAU)', value: '47%', delta: '+6 pts', up: true, spark: [38, 40, 41, 43, 45, 47] },
        { label: 'Support CSAT', value: '4.6/5', delta: '-0.1', up: false, spark: [4.7, 4.7, 4.6, 4.6, 4.7, 4.6] },
      ],
      footer: 'Source: product analytics and CSAT survey, Q3 FY26.',
    },
    {
      id: 'qbr-05', kind: 'trend', label: '05 · Trend', layout: 'area-callout',
      eyebrow: 'The trend', title: 'Adoption climbing, month on month',
      lede: 'Weekly active usage rose steadily through the quarter, with the step-change after the May feature release.',
      prefix: '', unit: 'k', source: 'Product analytics, weekly active users (000s).',
      points: [
        { x: 'Apr', v: 5.9 },
        { x: 'May', v: 6.4, note: 'Feature release' },
        { x: 'Jun', v: 7.1 },
        { x: 'Jul', v: 7.6 },
        { x: 'Aug', v: 8.4, note: 'Quarter high' },
      ],
    },
    {
      id: 'qbr-06', kind: 'features', label: '06 · Wins', layout: 'trio',
      eyebrow: 'Wins', title: 'Three things that moved the needle',
      cards: [
        { icon: 'trending', h: 'Smart booking suggestions', d: 'Shipped in May. Drove the step-change in active usage by removing friction from the daily booking flow.' },
        { icon: 'home', h: 'Floor-plan wayfinding', d: 'Closed the top adoption blocker raised in last QBR — new starters now find rooms and desks unaided.' },
        { icon: 'shield', h: 'Triage automation', d: 'Cut average resolution time and lifted first-response CSAT by routing issues to the right team on submission.' },
      ],
    },
    {
      id: 'qbr-07', kind: 'chart', label: '07 · Deep dive', layout: 'bars',
      eyebrow: 'Deep dive', title: 'Usage by module',
      lede: 'Where the engagement actually sits — and where the headroom is.',
      unit: '%', source: 'Feature engagement, % of active accounts, Q3 FY26.',
      bars: [
        { k: 'Booking', v: 72, note: 'Booking leads at <b>72%</b> of active accounts.' },
        { k: 'Visitors', v: 41, note: 'Visitor management at <b>41%</b>.' },
        { k: 'Issues', v: 33, note: 'Issue submission at <b>33%</b>.' },
        { k: 'Analytics', v: 28, note: 'Occupancy analytics at <b>28%</b>.' },
        { k: 'AI assistant', v: 19, note: 'Newest module, fastest-growing at <b>19%</b>.' },
      ],
    },
    {
      id: 'qbr-08', kind: 'kpi', label: '08 · Commercial', layout: 'row',
      eyebrow: 'Commercial', title: 'Pipeline and renewals',
      kpis: [
        { label: 'Renewals secured', value: '94%', delta: '+2 pts', up: true, spark: [89, 90, 91, 92, 93, 94] },
        { label: 'Expansion ARR', value: '£420k', delta: '+18%', up: true, spark: [310, 340, 360, 385, 405, 420] },
        { label: 'At-risk accounts', value: '3', delta: '-1', up: true, spark: [6, 5, 5, 4, 4, 3] },
      ],
      footer: 'Source: CRM pipeline and renewals report, Q3 FY26.',
    },
    {
      id: 'qbr-09', kind: 'bullets', label: '09 · Risks', layout: 'list',
      eyebrow: 'Risks', title: 'What we are watching',
      lede: 'Three items that could affect next quarter if left unmanaged.',
      items: [
        'CSAT dipped 0.1 — driven by support volume; mitigation in place',
        'One enterprise renewal at risk on integration scope',
        'AI adoption strong but concentrated in two accounts',
        'Hiring gap in implementation could slow new go-lives',
      ],
    },
    {
      id: 'qbr-10', kind: 'compare', label: '10 · Asks', layout: 'versus',
      eyebrow: 'The asks', title: 'What we need to go faster',
      left: {
        tag: 'If we do nothing', title: 'Status quo', highlight: false,
        items: ['Adoption plateaus mid-quarter', 'Two renewals slip to next quarter', 'AI stays concentrated'],
      },
      right: {
        tag: 'With support', title: 'What we are asking for', highlight: true,
        items: ['One implementation hire', 'Exec sponsor on the at-risk renewal', 'Joint AI rollout plan for top 10 accounts'],
      },
    },
    {
      id: 'qbr-11', kind: 'timeline', label: '11 · Next quarter', layout: 'horizontal',
      eyebrow: 'Next quarter', title: 'The Q4 FY26 plan',
      milestones: [
        { date: 'Month 1', t: 'Implementation hire', n: 'Owner: Tom Shaw. Target: two new go-lives unblocked.', active: true },
        { date: 'Month 2', t: 'At-risk renewal recovery', n: 'Owner: Maya Lindqvist. Target: renewal signed.' },
        { date: 'Month 3', t: 'AI rollout to top 10', n: 'Owner: Simon Clarke. Target: AI live in 8 of 10 accounts.' },
      ],
    },
    {
      id: 'qbr-12', kind: 'cta', label: '12 · Close', layout: 'ink-band',
      eyebrow: 'Q3 review',
      headline: 'One strong quarter.\nNow we compound it.',
      sub: 'Decisions agreed today, owners assigned, success measures set.',
      contact: [
        ['Owner', 'Tom Shaw'],
        ['Next review', 'Q4 FY26'],
      ],
    },
  ],
  notes: {
    'qbr-03': 'Lead with the 112% — frame the quarter as ahead of plan before drilling into the detail.',
    'qbr-04': 'CSAT is the one amber light; flag it now so slide 9 lands as expected, not as a surprise.',
    'qbr-10': 'This is the decision slide — pause here and secure agreement on the three asks before moving on.',
  },
};
